import React, { useState, useEffect } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Drawer from "@material-ui/core/Drawer";
import Link from "@material-ui/core/Link";
import InputLabel from "@material-ui/core/InputLabel";
import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import Chip from "@material-ui/core/Chip";
import { i18n } from "../../translate/i18n";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ContactDrawerSkeleton from "../ContactDrawerSkeleton";
import MarkdownWrapper from "../MarkdownWrapper";
import { CardHeader } from "@material-ui/core";
import ContactModal from "../ContactModal";
import ContactAvatar from "../ContactAvatar";
import { ContactNotes } from "../ContactNotes";
import OpportunityModal from "../OpportunityModal";

const drawerWidth = 320;

const useStyles = makeStyles(theme => ({
	drawer: {
		width: drawerWidth,
		flexShrink: 0,
	},
	drawerPaper: {
		width: drawerWidth,
		display: "flex",
		borderTop: "1px solid rgba(0, 0, 0, 0.12)",
		borderRight: "1px solid rgba(0, 0, 0, 0.12)",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		borderTopRightRadius: 4,
		borderBottomRightRadius: 4,
	},
	header: {
		display: "flex",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		backgroundColor: theme.palette.contactdrawer, //DARK MODE PLW DESIGN//
		alignItems: "center",
		padding: theme.spacing(0, 1),
		minHeight: "73px",
		justifyContent: "flex-start",
	},
	content: {
		display: "flex",
		backgroundColor: theme.palette.contactdrawer, //DARK MODE PLW DESIGN//
		flexDirection: "column",
		padding: "8px 0px 8px 8px",
		height: "100%",
		overflowY: "scroll",
		...theme.scrollbarStyles,
	},

	contactAvatar: {
		margin: 15,
		width: 100,
		height: 100,
	},

	contactHeader: {
		display: "flex",
		padding: 8,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		"& > *": {
			margin: 4,
		},
	},

	contactDetails: {
		marginTop: 8,
		padding: 8,
		display: "flex",
		flexDirection: "column",
	},
	contactExtraInfo: {
		marginTop: 4,
		padding: 6,
	},
}));

const ContactDrawer = ({ open, handleDrawerClose, contact, ticket, loading }) => {
	const classes = useStyles();

	const [modalOpen, setModalOpen] = useState(false);
	const [opportunities, setOpportunities] = useState([]);
	const [opportunityModalOpen, setOpportunityModalOpen] = useState(false);
	const [linkedOpportunityId, setLinkedOpportunityId] = useState(null);

	useEffect(() => {
		setLinkedOpportunityId(ticket?.opportunityId || null);
	}, [ticket]);

	const loadOpportunities = async () => {
		if (!contact?.id) return;
		try {
			const { data } = await api.get("/opportunities", {
				params: { contactId: contact.id },
			});
			setOpportunities(data.opportunities || []);
		} catch (err) {
			toastError(err);
		}
	};

	useEffect(() => {
		if (open && contact?.id) loadOpportunities();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, contact?.id]);

	const handleLinkTicket = async (opportunity, link) => {
		try {
			if (link) {
				await api.post(`/opportunities/${opportunity.id}/tickets/${ticket.id}`);
				setLinkedOpportunityId(opportunity.id);
				toast.success("Conversa vinculada ao negócio.");
			} else {
				await api.delete(`/opportunities/${opportunity.id}/tickets/${ticket.id}`);
				setLinkedOpportunityId(null);
				toast.success("Conversa desvinculada do negócio.");
			}
		} catch (err) {
			toastError(err);
		}
	};

	const handleOpportunityModalClose = async (saved) => {
		setOpportunityModalOpen(false);
		if (saved) {
			// negócio criado a partir da conversa já nasce vinculado a ela
			await handleLinkTicket(saved, true);
			loadOpportunities();
		}
	};

	return (
		<>
			<Drawer
				className={classes.drawer}
				variant="persistent"
				anchor="right"
				open={open}
				PaperProps={{ style: { position: "absolute" } }}
				BackdropProps={{ style: { position: "absolute" } }}
				ModalProps={{
					container: document.getElementById("drawer-container"),
					style: { position: "absolute" },
				}}
				classes={{
					paper: classes.drawerPaper,
				}}
			>
				<div className={classes.header}>
					<IconButton onClick={handleDrawerClose}>
						<CloseIcon />
					</IconButton>
					<Typography style={{ justifySelf: "center" }}>
						{i18n.t("contactDrawer.header")}
					</Typography>
				</div>
				{loading ? (
					<ContactDrawerSkeleton classes={classes} />
				) : (
					<div className={classes.content}>
						<Paper square variant="outlined" className={classes.contactHeader}>
							<CardHeader
								onClick={() => {}}
								style={{ cursor: "pointer", width: '100%' }}
								titleTypographyProps={{ noWrap: true }}
								subheaderTypographyProps={{ noWrap: true }}
								avatar={<ContactAvatar src={contact.profilePicUrl} name={contact.name} style={{ width: 60, height: 60 }} />}
								title={
									<Typography>{contact.name}</Typography>
								}
								subheader={
									<>
										<Typography style={{fontSize: 12}}>
											<Link href={`tel:${contact.number}`}>{contact.number}</Link>
										</Typography>
										<Typography style={{fontSize: 12}}>
											<Link href={`mailto:${contact.email}`}>{contact.email}</Link>
										</Typography>
									</>
								}
							/>
							<Button
								variant="outlined"
								color="primary"
								onClick={() => setModalOpen(true)}
								style={{fontSize: 12}}
							>
								{i18n.t("contactDrawer.buttons.edit")}
							</Button>
						</Paper>
						<Paper square variant="outlined" className={classes.contactDetails}>
							<Typography variant="subtitle1" style={{marginBottom: 10}}>
								{i18n.t("ticketOptionsMenu.appointmentsModal.title")}
							</Typography>
							<ContactNotes ticket={ticket} />
						</Paper>
						<Paper square variant="outlined" className={classes.contactDetails}>
							<div
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									marginBottom: 8,
								}}
							>
								<Typography variant="subtitle1">Negócios</Typography>
								<Button
									size="small"
									color="primary"
									onClick={() => setOpportunityModalOpen(true)}
									style={{ fontSize: 12 }}
								>
									Criar
								</Button>
							</div>
							{opportunities.length === 0 ? (
								<Typography variant="body2" color="textSecondary">
									Nenhum negócio com este contato.
								</Typography>
							) : (
								opportunities.map(opportunity => (
									<Paper
										key={opportunity.id}
										square
										variant="outlined"
										className={classes.contactExtraInfo}
									>
										<div
											style={{
												display: "flex",
												alignItems: "center",
												gap: 6,
												flexWrap: "wrap",
											}}
										>
											<Chip
												size="small"
												label={opportunity.stage?.name}
												style={{
													backgroundColor: `${opportunity.stage?.color || "#9E9E9E"}33`,
													color: opportunity.stage?.color,
													fontWeight: 600,
												}}
											/>
											<Typography
												variant="body2"
												style={{ fontWeight: 600, flex: 1, minWidth: 0 }}
												noWrap
											>
												{opportunity.title}
											</Typography>
										</div>
										{opportunity.value != null && opportunity.value !== "" && (
											<Typography variant="caption" color="textSecondary">
												{Number(opportunity.value).toLocaleString("pt-BR", {
													style: "currency",
													currency: "BRL",
												})}
											</Typography>
										)}
										<div style={{ marginTop: 4 }}>
											{linkedOpportunityId === opportunity.id ? (
												<Button
													size="small"
													onClick={() => handleLinkTicket(opportunity, false)}
													style={{ fontSize: 11 }}
												>
													Desvincular conversa
												</Button>
											) : (
												<Button
													size="small"
													color="primary"
													onClick={() => handleLinkTicket(opportunity, true)}
													style={{ fontSize: 11 }}
												>
													Vincular conversa
												</Button>
											)}
										</div>
									</Paper>
								))
							)}
						</Paper>
						<Paper square variant="outlined" className={classes.contactDetails}>
							<ContactModal
								open={modalOpen}
								onClose={() => setModalOpen(false)}
								contactId={contact.id}
							></ContactModal>
							<OpportunityModal
								open={opportunityModalOpen}
								onClose={handleOpportunityModalClose}
								initialContact={contact}
							/>
							<Typography variant="subtitle1">
								{i18n.t("contactDrawer.extraInfo")}
							</Typography>
							{contact?.extraInfo?.map(info => (
								<Paper
									key={info.id}
									square
									variant="outlined"
									className={classes.contactExtraInfo}
								>
									<InputLabel>{info.name}</InputLabel>
									<Typography component="div" noWrap style={{ paddingTop: 2 }}>
										<MarkdownWrapper>{info.value}</MarkdownWrapper>
									</Typography>
								</Paper>
							))}
						</Paper>
					</div>
				)}
			</Drawer>
		</>
	);
};

export default ContactDrawer;
